/**
 * How much the text is worth, before it is scored.
 *
 * **The owner decided on 2026-08-18 that the page scores any length.** It used to refuse below 432
 * letters, which is the rule item 31 set for the terminal tool: half the shortest training
 * passage. A refusal tells a reader nothing, and the honest answer is not "no" but "here is how
 * much to trust it".
 *
 * So the page draws a strength meter instead, in the manner of a password strength meter, and the
 * measurement behind it is real. `length_curve` in the artefact holds how well the shipped model
 * separated 320 AI passages from their 320 human sources at each length, and `strengthOf`
 * interpolates that curve. **No threshold is written into this file that the data does not set.**
 */

import type { DetectorMeta, LengthPoint } from './detector/types';

/**
 * The shortest text the page will score at all.
 *
 * This is a practical floor and not a measured one. A handful of letters gives a few n-grams, and
 * a verdict drawn from those is noise wearing a costume. The measured curve starts at 100 letters,
 * and below that the meter already reads its lowest band.
 */
export const MIN_LETTERS = 50;

/** One rung of the meter. */
export interface Strength {
  /** How many of the five bars are lit. */
  bars: number;
  /** What to call this much text. */
  label: string;
  /** The nudge under the meter, or `null` when there is nothing useful left to say. */
  nudge: string | null;
  /** The CSS suffix, which chooses the colour. */
  tone: 'none' | 'poor' | 'fair' | 'good' | 'best';
}

/**
 * Return how well the model separates text of this length, by reading the shipped curve.
 *
 * Below the first measured point the first value stands, and above the last the last one does.
 * Nothing is extrapolated: the curve is what was measured, and a number past its ends would be
 * invented here.
 */
export function accuracyAt(letters: number, curve: LengthPoint[]): number {
  if (curve.length === 0) throw new Error('the artefact carries no length curve');

  const first = curve[0];
  const last = curve[curve.length - 1];
  if (letters <= first.letters) return first.roc_auc;
  if (letters >= last.letters) return last.roc_auc;

  for (let i = 1; i < curve.length; i += 1) {
    const low = curve[i - 1];
    const high = curve[i];
    if (letters <= high.letters) {
      const across = (letters - low.letters) / (high.letters - low.letters);
      return low.roc_auc + across * (high.roc_auc - low.roc_auc);
    }
  }
  return last.roc_auc;
}

/**
 * Return one curve for the meter, from the detectors that name a writer.
 *
 * **The meter must not read the pooled detector.** That one answers "did a machine write this",
 * and it is the weakest of the three: its curve peaks at 0.847, so the top band never lights and
 * the fourth needs about 590 letters. A meter driven by it asks a reader for far more text than
 * the page actually needs.
 *
 * The two model detectors are what the page is for, and they reach the same accuracy on much
 * less text. The mean of their curves therefore drives the meter, at every measured length.
 *
 * The mean is taken rather than the best. Taking the best would describe the ChatGPT detector
 * alone, which reaches the top band at 320 letters, and the meter would then promise more than
 * the Claude reading beside it can deliver.
 */
export function meterCurve(metas: DetectorMeta[]): LengthPoint[] {
  const curves = metas.map((meta) => meta.length_curve).filter((curve) => curve.length > 0);
  if (curves.length === 0) throw new Error('no detector carries a length curve');
  if (curves.length === 1) return curves[0]!;

  // Every curve is measured at the same steps, because one run writes them all.
  const steps = curves[0]!.map((point) => point.letters);
  return steps.map((letters) => ({
    letters,
    roc_auc: curves.reduce((total, curve) => total + accuracyAt(letters, curve), 0) / curves.length,
  }));
}

/**
 * Return the rung of the meter for a text of this length.
 *
 * The bands are cut on the measured separation rather than on the letter count, so a better model
 * moves them on the next export without a line changing here.
 */
export function strengthOf(letters: number, source: DetectorMeta | LengthPoint[]): Strength {
  if (letters < MIN_LETTERS) {
    return {
      bars: 0,
      label: 'Not much to go on',
      nudge: 'Paste a paragraph or two.',
      tone: 'none',
    };
  }

  const curve = Array.isArray(source) ? source : source.length_curve;
  const accuracy = accuracyAt(letters, curve);

  if (accuracy < 0.68) {
    return {
      bars: 1,
      label: 'Wild guess territory',
      nudge: 'It will answer, but do not believe it. More text helps a lot.',
      tone: 'poor',
    };
  }
  if (accuracy < 0.75) {
    return {
      bars: 2,
      label: 'A rough read',
      nudge: 'Keep going. This gets noticeably better with more text.',
      tone: 'poor',
    };
  }
  if (accuracy < 0.8) {
    return {
      bars: 3,
      label: 'Getting somewhere',
      nudge: 'Worth a go. More text still helps.',
      tone: 'fair',
    };
  }
  if (accuracy < 0.85) {
    return {
      bars: 4,
      label: 'A decent read',
      nudge: 'A bit more text and it is about as good as it gets.',
      tone: 'good',
    };
  }
  return {
    bars: 5,
    label: 'About as good as it gets',
    nudge: null,
    tone: 'best',
  };
}
